import { useHover } from 'ahooks';
import type React from 'react';
import { useMemo, useRef } from 'react';
import { CollapseAnimation } from '../../CollapseAnimation';
import { Card, type CardProps } from '../Card';
import type { CardHeaderProps } from '../Card/CardHeader';
import { RuleCardContext } from './RuleCardContext';

export interface RuleCardProps
  extends Pick<CardHeaderProps, 'action'>,
    Pick<CardProps, 'selected' | 'onClick' | 'onMouseEnter' | 'onMouseLeave'> {
  open?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  header: React.ReactNode;
  content?: React.ReactNode;
  disabled?: boolean;
  expandable?: boolean;
  iconRight?: React.ReactNode;
  'aria-label'?: string;
  withBackground?: boolean;
  headerSelected?: boolean;
}

export const RuleCard = ({
  open,
  onOpenChange,
  header,
  content,
  action,
  selected,
  onClick,
  onMouseEnter,
  onMouseLeave,
  iconRight,
  expandable = true,
  withBackground = true,
  headerSelected = false,
  'aria-label': ariaLabel,
}: RuleCardProps) => {
  const contextValue = useMemo(() => ({ open }), [open]);
  const lineRef = useRef<HTMLDivElement>(null);
  const isHovering = useHover(lineRef);

  return (
    <RuleCardContext.Provider value={contextValue}>
      <Card
        withBackground={withBackground}
        isHovering={isHovering}
        ref={lineRef}
        allowHover={!open}
        selected={selected}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        aria-expanded={expandable ? open : undefined}
        aria-selected={selected}
        aria-label={ariaLabel}
      >
        <Card.Header
          selected={headerSelected}
          action={action}
          onClick={expandable ? () => onOpenChange?.(!open) : undefined}
          role={expandable ? 'button' : undefined}
          aria-label={expandable ? 'Toggle show card content' : undefined}
          iconRight={iconRight}
        >
          {header}
        </Card.Header>
        {expandable && (
          <CollapseAnimation open={open}>
            <Card.Content>{content}</Card.Content>
          </CollapseAnimation>
        )}
      </Card>
    </RuleCardContext.Provider>
  );
};
